import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { activateCard, activationErrorMessage, looksLikeCardId } from '../utils/cardActivation';
import '../styles/ActivatePage.css';
import '../styles/CardActivationExperience.css';
import { useToast } from '../contexts/ToastContext';
import { getManualActivationCopy } from '../utils/activationCopy';

const ActivatePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cardId, setCardId] = useState('');
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState('');
  const [autoActivating, setAutoActivating] = useState(false);
  const { showSuccess, showError } = useToast();
  const copy = getManualActivationCopy();

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const resolveActivationError = (error) => {
    if (!error.response) {
      return 'Unable to connect. Check your connection and try again.';
    }
    if (error.response.status === 500) {
      return 'Something went wrong. Please try again.';
    }
    return activationErrorMessage(error, 'Unable to link this card. Please try again.');
  };

  const autoActivateCard = useCallback(async (cardIdToActivate) => {
    if (!looksLikeCardId(cardIdToActivate)) {
      setError('Enter a valid card ID from your order confirmation.');
      return;
    }

    setAutoActivating(true);

    try {
      await activateCard(cardIdToActivate);

      localStorage.setItem('cardActivationSuccess', 'true');
      localStorage.removeItem('activationCardId');
      navigate('/my-card');
    } catch (activationError) {
      console.error('Auto-activation failed:', activationError);
      const userMessage = resolveActivationError(activationError);
      setError(userMessage);
      showError(userMessage);
      setAutoActivating(false);
    }
  }, [navigate, showError]);

  useEffect(() => {
    const cardIdFromUrl = searchParams.get('cardId');
    if (cardIdFromUrl) {
      setCardId(cardIdFromUrl);

      const token = localStorage.getItem('token');
      if (token) {
        setIsAuthenticated(true);
        autoActivateCard(cardIdFromUrl);
      } else {
        localStorage.setItem('activationCardId', cardIdFromUrl);
      }
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        setIsAuthenticated(true);
      }
    }
  }, [searchParams, autoActivateCard]);

  const isCardIdValid = looksLikeCardId(cardId);

  const handleActivate = async () => {
    if (!isCardIdValid) {
      setError('Enter the full card ID from your order confirmation email.');
      return;
    }

    if (!isAuthenticated) {
      localStorage.setItem('activationCardId', cardId);
      navigate('/login', { state: { returnUrl: `/activate?cardId=${encodeURIComponent(cardId)}`, activateCard: true } });
      return;
    }

    setIsActivating(true);
    setError('');

    try {
      await activateCard(cardId);

      localStorage.setItem('cardActivationSuccess', 'true');
      localStorage.removeItem('activationCardId');
      navigate('/my-card');
    } catch (activationError) {
      console.error('Activation failed:', activationError);
      const userMessage = resolveActivationError(activationError);
      setError(userMessage);
      showError(userMessage);
    } finally {
      setIsActivating(false);
    }
  };

  const handleCopyCardId = () => {
    navigator.clipboard.writeText(cardId);
    showSuccess('Card ID copied');
  };

  if (autoActivating) {
    return (
      <div className="velox-activation-page activate-page">
        <div className="velox-activation-page__inner">
          <div className="velox-activation-loading">
            <div className="velox-activation-loading__spinner" aria-hidden="true" />
            <p className="velox-activation-loading__title">{copy.autoTitle}</p>
            <p className="velox-activation-loading__subtitle">{copy.autoSubtitle}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="velox-activation-page activate-page">
      <div className="velox-activation-page__inner">
        <div className="velox-activation-shell activate-page__shell">
          <p className="velox-activation-wordmark">VELOX</p>
          <h1 className="velox-activation-title">{copy.title}</h1>
          <p className="velox-activation-subtitle">{copy.subtitle}</p>

          <div className="activate-page__field">
            <input
              type="text"
              id="cardId"
              name="cardId"
              className={`activate-page__input${isCardIdValid ? ' activate-page__input--valid' : cardId ? ' activate-page__input--invalid' : ''}`}
              placeholder="Card ID"
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
              autoComplete="off"
              spellCheck="false"
              autoCorrect="off"
              autoCapitalize="off"
              maxLength="50"
              autoFocus
              aria-label="Card ID"
            />
            {cardId && (
              <button
                type="button"
                className="activate-page__copy"
                onClick={handleCopyCardId}
                aria-label="Copy card ID"
              >
                Copy
              </button>
            )}
          </div>

          {error && <p className="velox-activation-error" role="alert">{error}</p>}

          <button
            type="button"
            className="velox-activation-cta"
            onClick={handleActivate}
            disabled={!isCardIdValid || isActivating}
          >
            {isActivating && <span className="velox-activation-cta__spinner" aria-hidden="true" />}
            {isActivating ? copy.ctaLoading : copy.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivatePage;
